import React, { useState, useEffect } from 'react';
import { Image } from 'react-konva';
import { getCompositeImageStoreImageByKey } from 'redux/reducers/experimentDataReducer';
import { FrequentlyUsedPlateSize } from 'components/canvas/constants';
import {
  selectAutoLevelLowerLimit,
  selectAutoLevelUpperLimit,
  selectLowerLevelLimit,
  selectUpperLevelLimit,
} from 'redux/selectors/StateSelectors';
import { useRMSelector } from 'redux/hooks';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { getTunedImageForPlate } from 'components/commandpanel/ImageTuning/utils';
import TilePainter from 'components/plateviewer/plate/TilePainter';
import { PlateImageStructure } from 'components/Experiment/constants';
import {
  useLastSelectedWellNavigationDetails,
  useOverrideCompositeImage,
} from 'redux/hooks/experiment';
import { useShowOverrideTuning, useWellDropImageMetaData } from 'redux/hooks/well';
import { usePlateDrawaingIdentifiers } from 'components/commandpanel/Navigator/hooks';

interface Props extends PlateImageStructure {
  imageTuningValues: ImageTuningValues;
  captureProfileId: number;
  zoomLevel: number;
  canvasRefs: any;
  plateRows: number;
  plateCols: number;
}

const PlateImagesRenderer = (props: Props) => {
  const {
    width,
    height,
    startX,
    startY,
    imageCompositeKeyString,
    captureProfileId,
    zoomLevel,
    plateRows,
    plateCols,
    plateId,
  } = props;
  const lowerLimit = useRMSelector((state) => selectLowerLevelLimit(state));
  const upperLimit = useRMSelector((state) => selectUpperLevelLimit(state));
  const autoLowerLimit = useRMSelector((state) => selectAutoLevelLowerLimit(state));
  const autoUpperLimit = useRMSelector((state) => selectAutoLevelUpperLimit(state));
  const plateImage = getCompositeImageStoreImageByKey(imageCompositeKeyString);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const getPlateDrawingIdentifiers = usePlateDrawaingIdentifiers();
  const wellDropMetaData = useWellDropImageMetaData();
  const plateDrawingIdentifiers = getPlateDrawingIdentifiers(plateId);
  const [canvasImage, setCanvasImage] = useState(
    plateImage === 'NoImage' ? null : plateImage,
  );
  const showOverride = useShowOverrideTuning();
  const getOverrideImage = useOverrideCompositeImage();
  const getImage = async () => {
    if (imageCompositeKeyString) {
      let tuningValue: ImageTuningValues;
      if (lowerLimit || upperLimit || autoLowerLimit || autoUpperLimit) {
        tuningValue = {
          lowerLevelLimit: lowerLimit,
          upperLevelLimit: upperLimit,
          autoLevelingLowerLimit: autoLowerLimit,
          autoLevelingUpperLimit: autoUpperLimit,
          isAutoLevelingOn: false,
        };
      }
      let image;
      if (showOverride && plateId > 0) {
        image = await getOverrideImage(plateId);
      } else {
        image = getCompositeImageStoreImageByKey(imageCompositeKeyString);
      }
      const tunedImage = getTunedImageForPlate(
        tuningValue,
        image == 'NoImage' ? null : image,
        captureProfileId,
        1,
      );
      setCanvasImage(tunedImage);
    } else {
      setCanvasImage(null);
    }
  };

  const loadTiles = () => {
    const allowedTilesCanvasSize = [
      FrequentlyUsedPlateSize.small,
      FrequentlyUsedPlateSize.medium,
      FrequentlyUsedPlateSize.max,
    ];
    if (
      (props !== undefined && zoomLevel === 0) ||
      plateRows * plateCols < FrequentlyUsedPlateSize.small ||
      !allowedTilesCanvasSize.includes(plateRows * plateCols) ||
      !wellNavigationDetails ||
      !canvasImage
    ) {
      return null;
    }

    return (
      <TilePainter
        key={`tile-${width}-${height}-${startX}-${startY}-${zoomLevel}`}
        {...props}
      />
    );
  };

  useEffect(() => {
    getImage();
  }, [
    imageCompositeKeyString,
    lowerLimit,
    upperLimit,
    autoLowerLimit,
    autoUpperLimit,
    showOverride,
    plateDrawingIdentifiers,
    wellDropMetaData,
  ]);
  return (
    <>
      <Image
        id="render-plate-imageview-render-img"
        key={`image-${width}-${height}-${startX}-${startY}`}
        x={startX}
        y={startY}
        height={height}
        width={width}
        image={canvasImage}
      />
      {loadTiles()}
    </>
  );
};

export default PlateImagesRenderer;
